import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useMiniprogramAnalytics } from '@/hooks/useMiniprogramAnalytics';
import {
  MiniprogramAnalyticsMobilePanel,
  type AnalyticsMpOption,
} from '@/components/miniprogramAnalytics/MiniprogramAnalyticsMobilePanel';
import { mobileAdmin } from '@/lib/mobileAdminPaths';

type MpRow = AnalyticsMpOption & { public_id?: string };

export function MobileAnalyticsPage() {
  const { mpId: mpIdParam } = useParams();
  const navigate = useNavigate();
  const { role } = useSession();
  const routeMpId = Math.max(0, parseInt(mpIdParam || '0', 10) || 0);

  const [days, setDays] = useState(14);
  const [mpList, setMpList] = useState<MpRow[]>([]);
  const [mpLoading, setMpLoading] = useState(true);

  const mpId = routeMpId > 0 ? routeMpId : mpList[0]?.id ?? 0;
  const { data, loading, refreshing, err, load } = useMiniprogramAnalytics(mpId, days);

  const loadMpList = useCallback(async () => {
    setMpLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: MpRow[] }>('list_miniprograms', {
        method: 'GET',
        query: { page: 1, per_page: 100 },
      });
      setMpList(r.ok ? r.items : []);
    } catch {
      setMpList([]);
    } finally {
      setMpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (role !== 'admin') return;
    void loadMpList();
  }, [role, loadMpList]);

  useEffect(() => {
    if (mpLoading || routeMpId > 0 || mpList.length === 0) return;
    navigate(mobileAdmin.analyticsFor(mpList[0].id), { replace: true });
  }, [mpLoading, routeMpId, mpList, navigate]);

  if (role !== 'admin') {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">访问统计</h1>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm leading-relaxed text-amber-900">
          访问统计仅系统管理员可用。请在电脑端登录管理员账号查看，或联系管理员开通权限。
        </div>
        <Link to={mobileAdmin.dashboard} className="inline-flex text-sm font-semibold text-teal-700 underline">
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <MiniprogramAnalyticsMobilePanel
      mpList={mpList}
      mpLoading={mpLoading}
      mpEmptyHint="暂无小程序。请先在电脑端「小程序管理」中创建并关联站点。"
      mpId={mpId}
      onSelectMp={(id) => navigate(mobileAdmin.analyticsFor(id))}
      days={days}
      onDaysChange={setDays}
      data={data}
      loading={loading}
      refreshing={refreshing}
      err={err}
      onRefresh={() => void load(true)}
      footerNote="UV 按 openid 或设备指纹去重；数据每 60 秒自动刷新。完整配置请使用电脑端小程序管理。"
    />
  );
}
